/**
 * 印刷用の栄養成分表（試用・2026-10-07）— `/print`。公開済みの公式値を、店頭掲示や御社サイト用の一覧にして印刷・PDF 保存する。
 *
 * 載せるのは承認済み（公開中）の公式値だけ。kalori の推定は載せない。
 * ⚠️ 表示の桁はエネルギー整数・ほかは小数第 1 位で揃えるだけ。食品表示基準の丸めや「0 と表示できる」規則への適合は
 *    御社でご確認ください（kalori は表示の適法性を判定しない）。
 */
import { useEffect, useMemo, useState } from "react";
import { ALLERGEN_MANDATORY } from "@kalori/business/brands";
import { api, type Me, type MenuEntry } from "../api";
import { Link } from "../router";

const fmt = (v: number | null, digits: number) => (v == null ? "—" : v.toLocaleString("ja-JP", { minimumFractionDigits: digits, maximumFractionDigits: digits }));

export function PrintTable() {
  const [entries, setEntries] = useState<MenuEntry[] | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [title, setTitle] = useState("栄養成分表");
  const [extra, setExtra] = useState(false);
  const [estimateNote, setEstimateNote] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([api.entries(), api.me()]).then(([e, m]) => {
      if (e.ok) setEntries(e.data.filter((x) => x.reviewState === "published").sort((a, b) => a.name.localeCompare(b.name, "ja")));
      else setError(e.error);
      if (m.ok) {
        setMe(m.data);
        setTitle(`${m.data.merchant.name} 栄養成分表`);
      }
    });
  }, []);

  const hasEstimated = useMemo(() => (entries ?? []).some((e) => e.valueBasis === "estimated" || e.valueBasis === "recipe_calculated"), [entries]);

  const allergenText = (e: MenuEntry) => {
    const items = e.allergenStates?.items ?? Object.fromEntries((e.allergens ?? []).map((a) => [a, "contains" as const]));
    const contains = Object.entries(items).filter(([, s]) => s === "contains").map(([k]) => k);
    const may = Object.entries(items).filter(([, s]) => s === "may_contain").map(([k]) => `※${k}`);
    return [...contains, ...may].join("・") || "—";
  };

  return (
    <section className="stack-lg print-page">
      <div className="no-print stack">
        <p className="small">
          <Link href="/entries">← 公式値の入力</Link>
        </p>
        <h1>
          印刷用の栄養成分表 <span className="badge trial">試用</span>
        </h1>
        <p className="muted">
          公開中の公式値を一覧にして印刷・PDF 保存できます（ブラウザの「印刷」から「PDF に保存」）。表示の桁はエネルギーを整数、ほかを小数第 1
          位に揃えています。食品表示基準の丸めの規則への適合は、御社でご確認ください。
        </p>
        <div className="toolbar">
          <label className="field">
            <span className="small">表の見出し</span>
            <input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="check-item">
            <input type="checkbox" checked={extra} onChange={(e) => setExtra(e.target.checked)} />
            糖質・食物繊維も載せる
          </label>
          <label className="check-item">
            <input type="checkbox" checked={estimateNote} onChange={(e) => setEstimateNote(e.target.checked)} />
            「この表示値は、目安です。」を添える{hasEstimated && "（計算・推定の値があります）"}
          </label>
        </div>
        <p>
          <button className="btn primary" onClick={() => window.print()}>
            印刷・PDF に保存
          </button>
        </p>
      </div>
      {error && <p className="error">{error}</p>}
      {entries && entries.length === 0 && <p className="muted">公開中の公式値はまだありません。</p>}
      {entries && entries.length > 0 && (
        <div className="print-sheet">
          <h2>{title}</h2>
          <table className="print-table">
            <thead>
              <tr>
                <th>メニュー</th>
                <th>エネルギー<br />(kcal)</th>
                <th>たんぱく質<br />(g)</th>
                <th>脂質<br />(g)</th>
                <th>炭水化物<br />(g)</th>
                {extra && <th>糖質<br />(g)</th>}
                {extra && <th>食物繊維<br />(g)</th>}
                <th>食塩相当量<br />(g)</th>
                <th>アレルゲン</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    {e.name}
                    {e.servingGrams != null && <span className="small">（1 食 {fmt(e.servingGrams, 0)}g）</span>}
                  </td>
                  <td className="num">{fmt(e.nutrition.cal, 0)}</td>
                  <td className="num">{fmt(e.nutrition.protein, 1)}</td>
                  <td className="num">{fmt(e.nutrition.fat, 1)}</td>
                  <td className="num">{fmt(e.nutrition.carbs, 1)}</td>
                  {extra && <td className="num">{fmt(e.nutrition.sugar, 1)}</td>}
                  {extra && <td className="num">{fmt(e.nutrition.fiber, 1)}</td>}
                  <td className="num">{fmt(e.nutrition.salt, 1)}</td>
                  <td>{allergenText(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="small">
            1 食あたりの値です。アレルゲンは特定原材料（{ALLERGEN_MANDATORY.join("・")}）ほか、御社が確認した品目を載せています（※はコンタミネーションの可能性）。
            {estimateNote && " この表示値は、目安です。"}
          </p>
          {me && <p className="small muted">{me.merchant.name}</p>}
        </div>
      )}
    </section>
  );
}
